import { listDispositionFlagsForWorkflow } from '@100mslive/disposition-engine';

import { resolveEngineWorkflowType } from './engineWorkflowType';

/**
 * Disposition flags only the AI agent ever sets during a call — the engine
 * deliberately excludes them from its label→flag forward map so no human
 * disposition selection can raise them. They are still RESET on a human
 * disposition save: the operator's selection is the source of truth for the
 * call's outcome, so a stale agent-set flag must not keep steering the
 * engine's recompute afterwards.
 */
export const AI_ONLY_DISPOSITION_FLAGS = [
  'call_made_after_working_hours',
  'needs_live_human',
] as const;

/**
 * The `output_data` boolean flags a human disposition save must reset for a
 * workflow, derived from `@100mslive/disposition-engine` rather than
 * hand-maintained: the union of every flag the workflow's dispositions can
 * set (`listDispositionFlagsForWorkflow`) plus the AI-only flags above,
 * sorted for determinism.
 *
 * Derivation replaced the old hardcoded `DISPOSITION_FLAGS` constant, which
 * silently drifted when BV-1871 grew the engine's vocabulary from 5 flags to
 * 17 — leaving contradictory stale flags (e.g. `tax_id_required` AND
 * `provider_info_mismatch` both true) in output_data. A future engine bump
 * now extends the reset automatically; `__tests__/resetFlags.test.ts` pins
 * the per-workflow lists so a vocabulary change fails loudly instead.
 *
 * Scoped per workflow on purpose: a flag outside the workflow's own catalog
 * (e.g. `provider_not_active` for pbm) can never have been set by that
 * workflow's UI or agent, and writing foreign keys into its output_data
 * would only add noise. Note `pbm_carveout` never appears here — it is a
 * *string* field (a PBM name like "Caremark") in the output schemas, not a
 * boolean, and the engine's forward map excludes it too.
 *
 * An unknown or undefined workflow_type degrades to just the AI-only flags,
 * mirroring `useDispositions`' graceful fallback — no dropdown option
 * carries a flag then, so nothing workflow-specific is in play.
 */
export function listDispositionResetFlags(workflowType: string | undefined): string[] {
  const flags = new Set<string>(AI_ONLY_DISPOSITION_FLAGS);
  if (workflowType) {
    try {
      const engineType = resolveEngineWorkflowType(workflowType);
      for (const flag of Object.values(listDispositionFlagsForWorkflow(engineType))) {
        flags.add(flag);
      }
    } catch {
      // Legacy case records may carry a workflow_type the engine doesn't
      // register — fall through with the AI-only flags.
    }
  }
  return [...flags].sort();
}
